import {
  ENTITY_TYPES,
  IAction,
  INotificationBoxItemBase,
  INotificationToaster,
  NotificationType,
} from '@shared/types';
import { generateId } from '@shared/utils';
import { get, has, includes, isEqual, keys } from 'lodash';

import {
  BULK_UPDATE_ERROR,
  BULK_UPDATE_SUCCESS,
  DELETE_DOCUMENT_BY_ENTITY_ID_ERROR,
  DELETE_DOCUMENT_BY_ENTITY_ID_SUCCESS,
  FETCH_GET_DOCUMENT_BY_ID_ERROR,
  FETCH_LIST_ERROR,
  FETCH_SCHEMA_ERROR,
  FETCH_CREATE_ENTITY_ERROR,
  FETCH_CREATE_ENTITY_SUCCESS,
  FETCH_UPDATE_ENTITY_ERROR,
  FETCH_UPDATE_ENTITY_SUCCESS,
  SEND_HTTP_REQUEST_ERROR,
  EventsActions,
  SessionActions,
  UiActions,
  XprtDataActions,
} from '../../actions';
import { FETCH_SCHEME_USED_ON_FIRST_RUN_ERROR } from '../ui/ui.reducer';
import {
  BAD_GATEWAY_STATUS_CODE,
  GATEWAY_TIMEOUT_STATUS_CODE,
} from '@config/index';
import { TOASTER_SUCCESS_MESSAGE } from '@shared/constants';
import { I18n } from '@app/i18n/strings-keys';

export interface INotificationsState {
  notifications: INotification[];
  latestNotification?: INotification;
}

export interface INotification {
  id: string;
  /**
   * error label for Error notifications, for example: "Failure", "Error", etc.
   * default value is "Failure" if type is "error", otherwise it is not shown
   */
  errorLabel?: string;
  title: string;
  body: string;
  entityType: string;
  type: string;
  code?: string;
  message?: string;
  translations?: {
    message?: string;
    title?: string;
    body?: object;
  };
  entity_type?: string;
  timeout?: number;
  toastId?: string;

  /**
   * Only Working for Error Notifications now
   *
   * u can use this to customize notification box options for each notification, for example:
   * notificationBoxOptions: {
   *   closeNotification: () => void, // <- function for closing notification
   *   onHover: () => void, // <- function for on hover notification
   *   onClick: () => void, // <- function for on click notification
   * }
   * if notificationBoxOptions is not provided, default options will be used:
   * closeNotification: () => void, // <- function for closing notification
   * onHover: () => void, // <- function for on hover notification
   * onClick: () => void, // <- function for on click notification
   *
   * */
  notificationBoxOptions?: Pick<
    INotificationBoxItemBase,
    'closeNotification' | 'onHover' | 'onClick'
  >;
}

const INITIAL_STATE: INotificationsState = {
  notifications: [],
};

const isLambdaTimeoutError: (
  error: ProgressEvent | { status: number },
) => boolean = (error): boolean =>
  error instanceof ProgressEvent ||
  get(error, 'status') === BAD_GATEWAY_STATUS_CODE ||
  get(error, 'status') === GATEWAY_TIMEOUT_STATUS_CODE;

const getErrorTitle: (payload: any) => string = (payload): boolean | any => {
  return (
    get(payload, 'errors.title') ||
    get(payload, 'errors.code') ||
    get(payload, 'errors.meta.code') ||
    get(payload, 'errors.status') ||
    get(payload, 'errors[0].message') ||
    get(payload, 'errors[0].meta.code') ||
    (isLambdaTimeoutError(payload.errors) &&
      I18n.ErrorMessages.APIRequestTimeoutTitle) ||
    get(payload, 'title') ||
    I18n.ErrorMessages.UnexpectedError
  );
};

// Todo: Verify usage - No references to this method found so far
const getErrorBody: (payload: any) => string = (payload): string => {
  if (isLambdaTimeoutError(payload.errors)) {
    return I18n.ErrorMessages.XVIAPIRequestError;
  }

  if (get(payload, 'errors.title') === get(payload, 'errors.meta.message')) {
    return '';
  }

  return (
    get(payload, 'errors.message') ||
    get(payload, 'errors.meta.message') ||
    get(payload, 'errors.statusText') ||
    I18n.ErrorMessages.UnexpectedError
  );
};

// Todo: Verify usage - No references to this method found so far
const generateErrorMessage = (payload: any): string => {
  if (!has(payload, 'message')) return getErrorBody(payload);

  return `${get(payload, 'message', I18n.ErrorMessages.UnexpectedError)}\n${getErrorBody(payload)}`;
};

/**
 * Find text and those translation in payload
 *
 * @param payload
 */
// eslint-disable-next-line
const findWithTranslationInPayloadFn =
  (payload: unknown) => (basePath: string[], propPath: string[]) => {
    const value = get(payload, [...basePath, ...propPath]);

    if (!value) return undefined;

    const data = get(payload, basePath, payload);
    const translation = findTranslationMetadata(data, propPath);

    return { value, translation };
  };
export type TranslationMetaProp = '_i18n_translations';
export type TranslationPath = ['meta', TranslationMetaProp];

export const translationProp: TranslationMetaProp = '_i18n_translations';
export const translationPath: TranslationPath = ['meta', translationProp];

/**
 * Find translationMeta in data object in translation path
 *
 * @param data
 * @param propPath
 */
// eslint-disable-next-line
export const findTranslationMetadata = (data, propPath) => {
  const metaData = get(data, [...translationPath, ...propPath]);

  if (!metaData || !isTranslationMeta(metaData)) return undefined;

  return metaData;
};

export const isTranslationMeta = (param: unknown) => {
  // map for enumeration all possible types with typescript check
  const metaMap = {
    plain: 1,
    'complex-template': 1,
    'only-params': 1,
    'list-template': 1,
    'no-translation': 1,
  };

  return includes(keys(metaMap), get(param, '_type')) && has(param, 'app_code');
};

export const prepareNotificationErrors: (payload: object) => INotification = (
  payload,
): INotification => {
  return {
    id: get(payload, 'errors.id') || get(payload, 'id') || generateId(),
    errorLabel: get(payload, 'errorLabel'),
    title: getErrorTitle(payload),
    body: generateErrorMessage(payload),
    entityType: get(payload, 'entity_type'),
    type: 'error',
    notificationBoxOptions: get(payload, 'notificationBoxOptions'),
  };
};

export const prepareNotificationSuccess: (payload: object) => INotification = (
  payload,
): INotification => {
  return {
    id: generateId(),
    title: get(payload, 'title') || TOASTER_SUCCESS_MESSAGE.title,
    body: get(payload, 'message', TOASTER_SUCCESS_MESSAGE.message),
    entityType: get(payload, 'entity_type'),
    type: 'success',
  };
};

// Todo: Verify usage - No references to this method found so far
export const prepareCartSaveNotification: (
  payload: INotificationToaster,
) => INotification = (payload): INotification => {
  const findWithTranslation = findWithTranslationInPayloadFn(payload);

  const titleWithTranslations = findWithTranslation([], ['title']);
  const messageWithTranslations = findWithTranslation([], ['message']);

  return {
    id: generateId(),
    title: titleWithTranslations?.value || 'UNEXPECTED ERROR',
    body: messageWithTranslations?.value || 'UNEXPECTED ERROR',
    translations: {
      body: messageWithTranslations?.translation,
      title: titleWithTranslations?.translation,
    },
    type: get(payload, 'type'),
    entityType: ENTITY_TYPES.CART,
  };
};

// eslint-disable-next-line complexity
export const notificationsReducer = (
  state: INotificationsState = INITIAL_STATE,
  action: IAction,
): INotificationsState => {
  const { payload } = action;

  const stateWithNewNotification: (notification: INotification) => any = (
    notification,
  ): INotificationsState => {
    return Object.assign({}, state, {
      notifications: [...state.notifications, notification],
      latestNotification: notification,
    });
  };

  switch (action.type) {
    // Saving requests notifications to the store
    case FETCH_SCHEMA_ERROR:
    case FETCH_LIST_ERROR:
    case FETCH_GET_DOCUMENT_BY_ID_ERROR:
    case DELETE_DOCUMENT_BY_ENTITY_ID_ERROR:
    case FETCH_CREATE_ENTITY_ERROR:
    case FETCH_UPDATE_ENTITY_ERROR:
    case SessionActions.FETCH_PERSONAL_TOKEN_ERROR:
    case SessionActions.FETCH_ACCESS_TOKEN_ERROR:
    case XprtDataActions.FETCH_COMPANIES_ERROR:
    case UiActions.GENERIC_ERROR_ACTION:
    case EventsActions.FETCH_EVENTS_LIST_ERROR:
    case SEND_HTTP_REQUEST_ERROR:
    case FETCH_SCHEME_USED_ON_FIRST_RUN_ERROR:
    case BULK_UPDATE_ERROR:
      if (get(payload, 'options.suppressErrors')) return state;
      if (
        get(payload, 'options.suppressTimeoutError', false) &&
        isLambdaTimeoutError(payload.errors)
      ) {
        return state;
      }

      const errorNotification: INotification =
        prepareNotificationErrors(payload);

      return stateWithNewNotification(errorNotification);
    case FETCH_CREATE_ENTITY_SUCCESS:
    case FETCH_UPDATE_ENTITY_SUCCESS:
    case BULK_UPDATE_SUCCESS:
      if (get(payload, 'options.suppressSuccessMessage')) return state;
      payload.title =
        get(payload, 'options.successNotificationMessage.title', null) ??
        payload.title;
      payload.message =
        get(payload, 'options.successNotificationMessage.message', null) ??
        payload.message;
      const successNotification: INotification =
        prepareNotificationSuccess(payload);

      return stateWithNewNotification(successNotification);

    case DELETE_DOCUMENT_BY_ENTITY_ID_SUCCESS:
      const deleteDocumentMessage: INotification = prepareNotificationSuccess({
        message: I18n.InfoMessages.ProductsWereSuccessfullyUpdated,
      });

      return stateWithNewNotification(deleteDocumentMessage);
    case UiActions.GENERIC_SUCCESS_ACTION:
      const notification: INotification = prepareNotificationSuccess(
        Object.assign({ message: 'success' }, payload),
      );

      return stateWithNewNotification(notification);

    case UiActions.GENERIC_TOASTER_ACTION:
      let genericNotification = payload;
      if (isEqual(payload.type, NotificationType.SUCCESS)) {
        genericNotification = { ...payload, id: generateId() };
      }

      return stateWithNewNotification(genericNotification);
    case UiActions.CART_CHANGES_VALIDATED_AND_APPLIED_ERROR:
      const cartNotification = prepareCartSaveNotification(payload);

      return stateWithNewNotification(cartNotification);

    default:
      return state;
  }
};
